import { EditorSettingService } from '@affine/core/modules/editor-settting';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { FeatureName } from '@affine/graphql';
import type { DocMode } from '@blocksuite/affine/blocks';
import {
  type DocProps,
  DocsService,
  useLiveData,
  useServices,
} from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

export const useDocHelper = () => {
  const {
    docsService,
    workbenchService,
    editorSettingService,
    workspaceQuotaService,
  } = useServices({
    DocsService,
    WorkbenchService,
    EditorSettingService,
    WorkspaceQuotaService,
  });

  const quota = useLiveData(workspaceQuotaService.quota.quota$);
  const max = useLiveData(workspaceQuotaService.quota.max$);
  const used = useLiveData(workspaceQuotaService.quota.used$);

  const workbench = workbenchService.workbench;
  const docRecordList = docsService.list;

  const createDocAndOpen = useCallback(
    (mode?: DocMode, open?: boolean | 'new-tab', docProps?: DocProps) => {
      // FIXME перенести в DocsService
      if (
        used.usedDocCount >= max.docLimit &&
        quota?.name !== FeatureName.unlimited
      ) {
        workspaceQuotaService.dialog.open();
        return;
      }

      const _docProps: DocProps = {
        note: editorSettingService.editorSetting.get('affine:note'),
        ...docProps,
      };
      const page = docsService.createDoc({ docProps: _docProps });
      if (mode) {
        docRecordList.doc$(page.id).value?.setPrimaryMode(mode);
      }

      if (open !== false)
        workbench.openDoc(page.id, {
          at: open === 'new-tab' ? 'new-tab' : 'active',
        });
      return page;
    },
    [
      docRecordList,
      docsService,
      editorSettingService.editorSetting,
      max.docLimit,
      quota?.name,
      used.usedDocCount,
      workbench,
      workspaceQuotaService.dialog,
    ]
  );

  const createEdgelessAndOpen = useCallback(
    (open?: boolean | 'new-tab') => {
      return createDocAndOpen('edgeless', open);
    },
    [createDocAndOpen]
  );

  return useMemo(() => {
    return {
      createDoc: (
        mode?: DocMode,
        open?: boolean | 'new-tab',
        docProps?: DocProps
      ) => createDocAndOpen(mode, open, docProps),
      createEdgeless: createEdgelessAndOpen,
    };
  }, [createEdgelessAndOpen, createDocAndOpen]);
};
